import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useCrowd, CrowdProvider } from './context/CrowdContext';
import { CameraModal } from './CameraModal';
import {
  BP,
  QLINES,
  QSTAGES,
  STAGE_ACTIVE_LINES,
  XSTAGES,
  BP_FEATURES,
  BP_EXITF,
  BP_ZONES,
  BP_APPROACH,
  BP_RETURN,
  GLYPH
} from './config/crowdConfig';
import {
  fmt,
  hhmm,
  losColor,
  cellDens,
  xDens,
  crowdWord,
  qKey
} from './utils/crowdMath';
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Move,
  Boxes
} from 'lucide-react';

export const DigitalTwinViewInner: React.FC = () => {
  const {
    clock,
    queueState,
    nodeRate,
    releaseRateVal,
    darshanRate,
    openCamera,
    logEvent,
    themeMode
  } = useCrowd();

  const isLight = themeMode === 'light';
  const bpBg = isLight ? '#FFFFFF' : '#0B131C';
  const bpGridStroke = isLight ? '#F1F5F9' : '#142332';
  const bpHatchStroke = isLight ? '#E2E8F0' : '#23445A';
  const bpTitleFill = isLight ? '#0F172A' : '#F1F5F9';
  const bpSubFill = isLight ? '#64748B' : '#94A3B8';
  const bpDimFill = isLight ? '#94A3B8' : '#64748B';
  const bpCardBg = isLight ? '#FFFFFF' : '#101B26';
  const bpCardBorder = isLight ? '#E2E8F0' : '#22384C';
  const bpTextMain = isLight ? '#0F172A' : '#F1F5F9';
  const bpTextMuted = isLight ? '#475569' : '#94A3B8';
  const bpAccentGold = isLight ? '#D97706' : '#F59E0B';
  const bpAccentCyan = isLight ? '#2563EB' : '#38BDF8';
  const bpBorderDash = isLight ? '#CBD5E1' : '#2D4B63';
  const bpBarricadeStroke = isLight ? '#E2E8F0' : '#1D3447';
  const bpGateBoxBg = isLight ? '#F8FAFC' : '#0D1824';
  const bpGateBoxBorder = isLight ? '#CBD5E1' : '#233E54';
  const bpGateItemBg = isLight ? '#FFFFFF' : '#12202F';
  const bpGateItemBorder = isLight ? '#E2E8F0' : '#254058';
  const bpGateTagBg = isLight ? '#EFF6FF' : '#1E3A5F';
  const bpGateTagBorder = isLight ? '#3B82F6' : '#60A5FA';
  const bpGateTagText = isLight ? '#1D4ED8' : '#93C5FD';
  const bpOutflowBadgeBg = isLight ? '#F1F5F9' : '#0E1D2B';
  const bpOutflowBadgeBorder = isLight ? '#CBD5E1' : '#233E54';
  const bpCamCircleFill = isLight ? '#EFF6FF' : '#152536';
  const bpCamCircleStroke = isLight ? '#3B82F6' : '#38BDF8';
  const bpCamIconFill = isLight ? '#2563EB' : '#38BDF8';

  // Unified Transform State (scale, panX, panY) with instant Ref synchronization
  const [transform, setTransform] = useState<{ scale: number; x: number; y: number }>({
    scale: 0.55,
    x: 40,
    y: 10
  });
  const transformRef = useRef<{ scale: number; x: number; y: number }>(transform);
  transformRef.current = transform;

  const [isPanning, setIsPanning] = useState<boolean>(false);
  const panStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragStartPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const hasMovedRef = useRef<boolean>(false);
  const lastTouchDistRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-fit and center blueprint within viewport
  const resetToFit = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const padX = 20;
    const padY = 20;
    const availW = rect.width - padX * 2;
    const availH = rect.height - padY * 2;

    const scale = Math.min(availW / BP.W, availH / BP.H);
    const fitScale = Math.min(2.5, Math.max(0.15, Number(scale.toFixed(3))));

    const panX = (rect.width - BP.W * fitScale) / 2;
    const panY = (rect.height - BP.H * fitScale) / 2;

    const next = {
      scale: fitScale,
      x: Number(panX.toFixed(2)),
      y: Number(panY.toFixed(2))
    };
    transformRef.current = next;
    setTransform(next);
  }, []);

  // Initial auto-fit on load & window resize & container layout observer
  useEffect(() => {
    resetToFit();
    const raf1 = requestAnimationFrame(() => resetToFit());
    const t1 = setTimeout(() => resetToFit(), 60);
    const t2 = setTimeout(() => resetToFit(), 200);

    let ro: ResizeObserver | null = null;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        resetToFit();
      });
      ro.observe(containerRef.current);
    }

    const handleResize = () => resetToFit();
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(raf1);
      clearTimeout(t1);
      clearTimeout(t2);
      ro?.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [resetToFit]);

  // Precise cursor-anchored zoom with instant ref sync
  const zoomAtPoint = useCallback((targetScaleRaw: number, clientX?: number, clientY?: number) => {
    const newScale = Math.min(4.0, Math.max(0.25, Number(targetScaleRaw.toFixed(3))));
    const current = transformRef.current;
    if (!containerRef.current) {
      const next = { ...current, scale: newScale };
      transformRef.current = next;
      setTransform(next);
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = clientX !== undefined ? clientX - rect.left : rect.width / 2;
    const mouseY = clientY !== undefined ? clientY - rect.top : rect.height / 2;

    const ratio = newScale / current.scale;
    const newX = mouseX - (mouseX - current.x) * ratio;
    const newY = mouseY - (mouseY - current.y) * ratio;

    const next = {
      scale: newScale,
      x: Number(newX.toFixed(2)),
      y: Number(newY.toFixed(2))
    };
    transformRef.current = next;
    setTransform(next);
  }, []);

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    hasMovedRef.current = false;
    dragStartPosRef.current = { x: e.clientX, y: e.clientY };
    panStartRef.current = {
      x: e.clientX - transformRef.current.x,
      y: e.clientY - transformRef.current.y
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanning) return;
    const dx = e.clientX - dragStartPosRef.current.x;
    const dy = e.clientY - dragStartPosRef.current.y;
    if (Math.hypot(dx, dy) > 4) {
      hasMovedRef.current = true;
    }
    const nextX = Number((e.clientX - panStartRef.current.x).toFixed(2));
    const nextY = Number((e.clientY - panStartRef.current.y).toFixed(2));
    const next = {
      ...transformRef.current,
      x: nextX,
      y: nextY
    };
    transformRef.current = next;
    setTransform(next);
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Wheel zoom (anchored directly to the exact cursor position)
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const factor = e.deltaY < 0 ? 1.14 : 0.88;
    zoomAtPoint(transformRef.current.scale * factor, e.clientX, e.clientY);
  };

  // Double-click to zoom in at cursor or reset if deep zoomed
  const handleDoubleClick = (e: React.MouseEvent) => {
    if (hasMovedRef.current) return;
    if (transformRef.current.scale > 1.8) {
      resetToFit();
    } else {
      zoomAtPoint(transformRef.current.scale * 1.6, e.clientX, e.clientY);
    }
  };

  // Touch handlers for mobile & pinch-to-zoom
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setIsPanning(true);
      hasMovedRef.current = false;
      const t = e.touches[0];
      dragStartPosRef.current = { x: t.clientX, y: t.clientY };
      panStartRef.current = {
        x: t.clientX - transformRef.current.x,
        y: t.clientY - transformRef.current.y
      };
    } else if (e.touches.length === 2) {
      setIsPanning(false);
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      lastTouchDistRef.current = dist;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && isPanning) {
      const t = e.touches[0];
      const dx = t.clientX - dragStartPosRef.current.x;
      const dy = t.clientY - dragStartPosRef.current.y;
      if (Math.hypot(dx, dy) > 4) {
        hasMovedRef.current = true;
      }
      const nextX = Number((t.clientX - panStartRef.current.x).toFixed(2));
      const nextY = Number((t.clientY - panStartRef.current.y).toFixed(2));
      const next = {
        ...transformRef.current,
        x: nextX,
        y: nextY
      };
      transformRef.current = next;
      setTransform(next);
    } else if (e.touches.length === 2 && lastTouchDistRef.current !== null) {
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const ratio = dist / lastTouchDistRef.current;
      lastTouchDistRef.current = dist;
      zoomAtPoint(transformRef.current.scale * ratio, (t1.clientX + t2.clientX) / 2, (t1.clientY + t2.clientY) / 2);
    }
  };

  const handleTouchEnd = () => {
    setIsPanning(false);
    lastTouchDistRef.current = null;
  };

  const zoomIn = () => {
    zoomAtPoint(transformRef.current.scale * 1.25);
  };

  const zoomOut = () => {
    zoomAtPoint(transformRef.current.scale * 0.8);
  };

  const setPresetZoom = (presetScale: number) => {
    if (!containerRef.current) {
      setTransform(p => ({ ...p, scale: presetScale }));
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const current = transformRef.current;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const ratio = presetScale / current.scale;
    const newX = centerX - (centerX - current.x) * ratio;
    const newY = centerY - (centerY - current.y) * ratio;

    const next = {
      scale: presetScale,
      x: Number(newX.toFixed(2)),
      y: Number(newY.toFixed(2))
    };
    transformRef.current = next;
    setTransform(next);
  };

  // Keyboard navigation & zoom shortcuts (+, -, 0, Escape)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        zoomOut();
      } else if (e.key === '0' || e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        resetToFit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoomIn, zoomOut, resetToFit]);

  // Click & Drag visual cursor state
  const getCursorStyle = () => {
    if (isPanning) return 'cursor-grabbing';
    return 'cursor-grab';
  };

 

  // Safe click handler (prevents click firing right after dragging)
  const onSafeClick = (cb: () => void) => {
    if (!hasMovedRef.current) {
      cb();
    }
  };

  // Blueprint stage calculations
  const bpStages = () => {
    let x = BP.x0;
    return QSTAGES.map(st => {
      const o = { st, x, w: st.len };
      x += st.len;
      return o;
    });
  };

  const bpExitStages = () => {
    // Dynamically align with the right edge of S-9 + Sanctum exit flow
    let x = right + 130;
    return XSTAGES.map(st => {
      x -= st.len;
      return { st, x, w: st.len };
    });
  };

  const bpLaneY = (i: number) => BP.y0 + i * BP.lane;

  const S1 = bpStages();
  const right = S1[S1.length - 1].x + S1[S1.length - 1].w;
  const X1 = bpExitStages();
  const top = BP.y0 - 10;
  const bh = QLINES.length * BP.lane + 6;

  const q5 = S1[4];
  const q6 = S1[5];

  const zw = (BP.W - BP.x0 - 60) / BP_ZONES.length;
  const aw = (right - BP.x0) / BP_APPROACH.length;
  const ry = BP.exitY + BP.exitH + 92;
  const rw = (right - BP.x0) / BP_RETURN.length;

  const sx = BP.x0;
  const sy = BP.H - 56;

  // Values for approach sectors
  const getApproachVal = (index: number) => {
    const a = BP_APPROACH[index];
    if (a.hold) return { text: `${fmt(queueState.hold)} held`, color: queueState.hold > 6000 ? (isLight ? "#DC2626" : "#FF8D82") : bpAccentGold };
    if (a.c === "S-30") {
      const rate = ["STN-MAIN", "STN-THARA", "STN-ET2", "STN-PARCEL"].reduce((acc, i) => acc + (nodeRate[i] || 0), 0);
      return { text: `${fmt(rate)}/min`, color: isLight ? "#059669" : "#25D0A3" };
    }
    if (a.c === "S-29") return { text: `${fmt(nodeRate["PNBS"] || 0)}/min`, color: isLight ? "#059669" : "#25D0A3" };
    if (a.c === "S-28") return { text: `${fmt((nodeRate["LOCAL"] || 0) * 0.34)}/min`, color: isLight ? "#059669" : "#25D0A3" };
    if (a.c === "S-24") return { text: `${fmt((nodeRate["LOCAL"] || 0) * 0.41)}/min`, color: isLight ? "#059669" : "#25D0A3" };
    if (a.c === "S-25") return { text: `${fmt((nodeRate["LOCAL"] || 0) * 0.11)}/min`, color: isLight ? "#059669" : "#25D0A3" };
    return { text: `${fmt(queueState.holdOpen ? releaseRateVal : 0)}/min`, color: isLight ? "#059669" : "#25D0A3" };
  };

  return (
    <div className="w-full h-[calc(100vh-6.75rem)] max-h-[calc(100vh-6.75rem)] min-h-[580px] flex flex-col rounded-xl border border-neutral-200/80 dark:border-[#262b37] overflow-hidden bg-[#f4f5f8] dark:bg-[#0e1015] text-neutral-800 dark:text-neutral-100 relative shadow-xs transition-colors duration-200">
      {/* Top Floating Control Bar */}
      <div className="shrink-0 px-4 py-2.5 border-b border-neutral-200/80 dark:border-[#262b37] bg-white dark:bg-[#181b22] flex flex-wrap items-center justify-between gap-3 z-30 shadow-xs">
        {/* Title / Identity */}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg border bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400">
            <Boxes className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[15px] font-bold tracking-wide uppercase text-neutral-900 dark:text-neutral-100">
                Darshan Queue Digital Twin
              </span>
              <span className="px-2 py-0.5 rounded text-[10.5px] font-mono font-bold bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30">
                2D BLUEPRINT MATRIX
              </span>
            </div>
            <div className="text-[11.5px] text-neutral-500 dark:text-neutral-400 flex items-center gap-2 mt-0.5">
              <span>Full scale architectural schematics &amp; live sector telemetry</span>
            </div>
          </div>
        </div>

        {/* Interaction controls: Presets, Zoom In/Out, Reset */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Helpful gesture badge */}
          <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-neutral-100 dark:bg-[#222733] border border-neutral-200/60 dark:border-[#2a3040] text-neutral-500 dark:text-neutral-400 text-[11px] font-mono">
            <Move className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
            <span>Drag to pan · Scroll to zoom</span>
          </div>

          {/* Quick Presets */}
          <div className="flex items-center bg-neutral-100 dark:bg-[#222733] border border-neutral-200/60 dark:border-[#2a3040] rounded-lg p-0.5 text-[11px] font-mono text-neutral-600 dark:text-neutral-400">
            <button
              onClick={() => setPresetZoom(0.75)}
              className={`px-2 py-1 rounded-md transition cursor-pointer ${
                Math.abs(transform.scale - 0.75) < 0.05
                  ? 'bg-blue-600 text-white font-bold shadow-xs'
                  : 'hover:text-neutral-900 dark:hover:text-neutral-100'
              }`}
            >
              75%
            </button>
            <button
              onClick={() => setPresetZoom(1)}
              className={`px-2 py-1 rounded-md transition cursor-pointer ${
                Math.abs(transform.scale - 1) < 0.05
                  ? 'bg-blue-600 text-white font-bold shadow-xs'
                  : 'hover:text-neutral-900 dark:hover:text-neutral-100'
              }`}
            >
              100%
            </button>
            <button
              onClick={() => setPresetZoom(1.5)}
              className={`px-2 py-1 rounded-md transition cursor-pointer ${
                Math.abs(transform.scale - 1.5) < 0.05
                  ? 'bg-blue-600 text-white font-bold shadow-xs'
                  : 'hover:text-neutral-900 dark:hover:text-neutral-100'
              }`}
            >
              150%
            </button>
            <button
              onClick={() => setPresetZoom(2)}
              className={`px-2 py-1 rounded-md transition cursor-pointer ${
                Math.abs(transform.scale - 2) < 0.05
                  ? 'bg-blue-600 text-white font-bold shadow-xs'
                  : 'hover:text-neutral-900 dark:hover:text-neutral-100'
              }`}
            >
              200%
            </button>
          </div>

          {/* Zoom controls */}
          <div className="flex items-center gap-1.5 bg-neutral-100 dark:bg-[#222733] border border-neutral-200/60 dark:border-[#2a3040] rounded-lg p-1">
            <span className="text-[11px] font-mono text-neutral-600 dark:text-neutral-300 px-1 min-w-[42px] text-center font-bold">
              {Math.round(transform.scale * 100)}%
            </span>
            <button
              onClick={zoomOut}
              className="p-1 rounded-md bg-white dark:bg-[#181b22] border border-neutral-200/60 dark:border-[#262b37] text-neutral-600 dark:text-neutral-300 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-500/40 transition cursor-pointer active:scale-95 shadow-2xs"
              title="Zoom Out"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={zoomIn}
              className="p-1 rounded-md bg-white dark:bg-[#181b22] border border-neutral-200/60 dark:border-[#262b37] text-neutral-600 dark:text-neutral-300 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-500/40 transition cursor-pointer active:scale-95 shadow-2xs"
              title="Zoom In"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={resetToFit}
              className="p-1 rounded-md bg-white dark:bg-[#181b22] border border-neutral-200/60 dark:border-[#262b37] text-neutral-600 dark:text-neutral-300 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-500/40 transition cursor-pointer active:scale-95 flex items-center gap-1.5 text-[11px] font-mono px-2 shadow-2xs"
              title="Fit &amp; Center Blueprint"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Fit Screen</span>
            </button>
          </div>
        </div>
      </div>

      {/* Interactive Blueprint Canvas Viewport */}
      <div
        id="blueprint-viewport"
        ref={containerRef}
        className={`flex-1 min-h-0 w-full relative overflow-hidden bg-[#f4f5f8] dark:bg-[#0e1015] select-none ${getCursorStyle()}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onDoubleClick={handleDoubleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          style={{
            width: `${BP.W}px`,
            height: `${BP.H}px`,
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`,
            transformOrigin: '0 0',
            willChange: 'transform'
          }}
          className="absolute top-0 left-0"
        >
          <svg
            viewBox={`0 0 ${BP.W} ${BP.H}`}
            width={BP.W}
            height={BP.H}
            className="w-full h-full block drop-shadow-2xl"
            preserveAspectRatio="xMidYMid meet"
          >
          <defs>
            <pattern id="bpgrid" width="25" height="25" patternUnits="userSpaceOnUse">
              <path d="M25 0 L0 0 0 25" fill="none" stroke={bpGridStroke} strokeWidth="0.6" />
            </pattern>
            <pattern id="bphatchp" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="7" stroke={bpHatchStroke} strokeWidth="1.1" />
            </pattern>
          </defs>

          {/* Background */}
          <rect width={BP.W} height={BP.H} fill={bpBg} />
          <rect width={BP.W} height={BP.H} fill="url(#bpgrid)" />

          {/* Headers */}
          <text
            x={BP.x0}
            y="58"
            fill={bpTitleFill}
            fontFamily="var(--font-disp, 'Outfit', sans-serif)"
            fontSize="19"
            letterSpacing="0.08em"
            fontWeight="bold"
          >
            DARSHAN QUEUE SYSTEM — PLAN
          </text>
          <text
            x={BP.x0}
            y="78"
            fill={bpSubFill}
            fontSize="11"
            letterSpacing="0.03em"
          >
            దర్శన క్యూలైన్ల ప్రణాళిక · 5 free lanes + Sheegra, VIP, VVIP and service lane
          </text>
          <text
            x={BP.x0}
            y="100"
            fill={bpDimFill}
            fontFamily="var(--font-mono, monospace)"
            fontSize="9.5"
            letterSpacing="0.24em"
          >
            RIVER / GHAT SIDE ◀ — — — ENTRY FLOW — — — ▶ HILL &amp; TEMPLE
          </text>

          {/* Zone Key Strip */}
          <g>
            {BP_ZONES.map((z, i) => {
              const x = BP.x0 + i * zw;
              return (
                <g key={z.z}>
                  <rect x={x} y="118" width={zw - 6} height="30" fill={bpCardBg} stroke={bpCardBorder} strokeWidth="1" rx="2" />
                  <text
                    x={x + 8}
                    y={131}
                    fill={bpAccentGold}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="9"
                    fontWeight="bold"
                    letterSpacing="0.06em"
                  >
                    ZONE {z.z}
                  </text>
                  <text x={x + 60} y={131} fill={bpTextMain} fontSize="9.5" fontWeight="bold">{z.n}</text>
                  <text x={x + 8} y={143} fill={bpSubFill} fontSize="8">{z.s} · {z.te}</text>
                </g>
              );
            })}
          </g>

          {/* Approach Band */}
          <g>
            <text
              x={BP.x0}
              y={176}
              fill={bpTitleFill}
              fontFamily="var(--font-disp, 'Outfit', sans-serif)"
              fontSize="11"
              letterSpacing="0.08em"
              fontWeight="bold"
            >
              APPROACH — devotee reaches a queue line · సెక్టార్లు S-20 … S-3
            </text>
            {BP_APPROACH.map((a, i) => {
              const x = BP.x0 + i * aw;
              const y = 186;
              const val = getApproachVal(i);
              const lines = a.lines || (a.n.length > 14 ? [a.n.slice(0, a.n.lastIndexOf(' ', 14)), a.n.slice(a.n.lastIndexOf(' ', 14) + 1)] : [a.n]);
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={y}
                    width={aw - 7}
                    height="54"
                    fill={a.hold ? (isLight ? "#FEF3C7" : "#1D1708") : bpCardBg}
                    stroke={a.hold ? bpAccentGold : bpCardBorder}
                    strokeWidth={a.hold ? 1.6 : 1}
                    rx="2"
                  />
                  <text
                    x={x + 6}
                    y={y + 13}
                    fill={bpAccentGold}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="8.5"
                    fontWeight="bold"
                  >
                    {a.c}
                  </text>
                  {lines.map((line, lIdx) => (
                    <text
                      key={lIdx}
                      x={x + 6}
                      y={lines.length === 1 ? y + 27 : y + 23 + lIdx * 10}
                      fill={bpTextMuted}
                      fontSize="7.5"
                    >
                      {line}
                    </text>
                  ))}
                  <text
                    x={x + 6}
                    y={y + 47}
                    fill={val.color}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="9.5"
                    fontWeight="bold"
                  >
                    {val.text}
                  </text>
                  {i < BP_APPROACH.length - 1 && (
                    <path d={`M${x + aw - 6},${y + 27} l-5,-4 v8 z`} fill={bpBorderDash} />
                  )}
                  <title>{`${a.c}: ${a.n} — ${val.text}`}</title>
                </g>
              );
            })}
            <path d={`M${BP.x0 + 2},244 V${BP.y0 - 118} H${right}`} fill="none" stroke={bpBorderDash} strokeWidth="0.9" strokeDasharray="4 4" />
            <text
              x={BP.x0 + 10}
              y={BP.y0 - 124}
              fill={bpSubFill}
              fontFamily="var(--font-mono, monospace)"
              fontSize="8.5"
              letterSpacing="0.1em"
            >
              JOINS THE Q-LINE BUNDLE AT S-4 KUMMARIPALEM ▼
            </text>
          </g>

          {/* Barricades */}
          <g>
            {QLINES.map((_, i) => (
              <line
                key={i}
                x1={BP.x0}
                y1={bpLaneY(i) - 4}
                x2={right}
                y2={bpLaneY(i) - 4}
                stroke={bpBarricadeStroke}
                strokeWidth="0.9"
                strokeDasharray="5 4"
              />
            ))}
            <line
              x1={BP.x0}
              y1={bpLaneY(QLINES.length) - 4}
              x2={right}
              y2={bpLaneY(QLINES.length) - 4}
              stroke={bpBarricadeStroke}
              strokeWidth="0.9"
              strokeDasharray="5 4"
            />
          </g>

          {/* Frames & Dimensions */}
          <g>
            {S1.map(o => {
              const activeCount = STAGE_ACTIVE_LINES[o.st.id]?.length || 0;
              const activeBadge = activeCount === 5 ? "ALL 5 Qs" : `${activeCount} Qs`;

              // Stage detailed multi-line definitions for full readability without overlapping
              const stageDetails: Record<
                string,
                { line1: string; line2?: string; te: string }
              > = {
                QS1: {
                  line1: "VINAYAKA TEMPLE TO TOLL GATE",
                  te: "వినాయక గుడి నుండి టోల్‌గేట్"
                },
                QS2: {
                  line1: "KUMMARIPALEM TO",
                  line2: "HEAD WATER WORKS",
                  te: "కుమ్మరిపాలెం → హెడ్ వాటర్ వర్క్స్"
                },
                QS3: {
                  line1: "HEAD WATER WORKS TO",
                  line2: "TOLL GATE",
                  te: "హెడ్ వాటర్ వర్క్స్ → టోల్‌గేట్"
                },
                QS4: {
                  line1: "TOLL GATE TO OM TURNING",
                  te: "టోల్‌గేట్ నుండి ఓం టర్నింగ్"
                },
                QS5: {
                  line1: "OM TURNING TO",
                  line2: "OLD RAJAGOPURAM",
                  te: "ఓం టర్నింగ్ నుండి పాత రాజగోపురం"
                },
                QS6: {
                  line1: "OLD RAJAGOPURAM TO",
                  line2: "ANTHARALAYAM EXIT",
                  te: "రాజగోపురం నుండి అంతరాలయం"
                }
              };

              const dt = stageDetails[o.st.id] || { line1: o.st.name.toUpperCase(), te: o.st.te };

              return (
                <g key={o.st.id}>
                  {/* Stage Border Frame */}
                  <rect x={o.x - 1} y={top} width={o.w} height={bh} fill="none" stroke={bpCardBorder} strokeWidth="1" rx="2" />

                  {/* Stage Titles & Route Info */}
                  {dt.line2 ? (
                    <>
                      {/* 2-Line Stacked Title for compact stages */}
                      <text
                        x={o.x + 4}
                        y={top - 28}
                        fill={bpDimFill}
                        fontSize="7"
                      >
                        {dt.te}
                      </text>
                      <text
                        x={o.x + 4}
                        y={top - 17}
                        fontFamily="var(--font-mono, monospace)"
                        fontSize="7.5"
                      >
                        <tspan fill={bpAccentGold} fontWeight="bold">{o.st.sector}</tspan>
                        <tspan fill={bpAccentCyan} letterSpacing="0.02em" dx="4" fontWeight="bold">
                          {dt.line1}
                        </tspan>
                      </text>
                      <text
                        x={o.x + 4}
                        y={top - 7}
                        fontFamily="var(--font-mono, monospace)"
                        fontSize="7.5"
                      >
                        <tspan fill={bpAccentCyan} letterSpacing="0.02em" fontWeight="bold">
                          {dt.line2}
                        </tspan>
                        <tspan fill={bpSubFill} fontSize="6.5" dx="4">
                          [{activeBadge}]
                        </tspan>
                      </text>
                    </>
                  ) : (
                    <>
                      {/* 1-Line Wide Stage Title */}
                      <text
                        x={o.x + 4}
                        y={top - 20}
                        fill={bpDimFill}
                        fontSize="7.5"
                      >
                        {dt.te}
                      </text>
                      <text
                        x={o.x + 4}
                        y={top - 8}
                        fontFamily="var(--font-mono, monospace)"
                        fontSize="8"
                      >
                        <tspan fill={bpAccentGold} fontWeight="bold">{o.st.sector}</tspan>
                        <tspan fill={bpAccentCyan} letterSpacing="0.03em" dx="4" fontWeight="bold">
                          {dt.line1}
                        </tspan>
                        <tspan fill={bpSubFill} fontSize="7" dx="4">
                          [{activeBadge}]
                        </tspan>
                      </text>
                    </>
                  )}

                  {/* Stage Width Metric Bar */}
                  <line x1={o.x} y1={top + bh + 30} x2={o.x + o.w} y2={top + bh + 30} stroke={bpBorderDash} strokeWidth="0.8" />
                  <line x1={o.x} y1={top + bh + 25} x2={o.x} y2={top + bh + 35} stroke={bpBorderDash} strokeWidth="0.8" />
                  <line x1={o.x + o.w} y1={top + bh + 25} x2={o.x + o.w} y2={top + bh + 35} stroke={bpBorderDash} strokeWidth="0.8" />
                  <text
                    x={o.x + o.w / 2}
                    y={top + bh + 26}
                    textAnchor="middle"
                    fill={bpSubFill}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="8.5"
                  >
                    {o.st.len} m
                  </text>
                </g>
              );
            })}
          </g>

          {/* Core / Sanctum */}
          <g>
            <rect
              x={q5.x}
              y={top - 100}
              width={q5.w}
              height="44"
              fill="url(#bphatchp)"
              stroke={bpHatchStroke}
              strokeWidth="1.2"
              opacity="0.55"
            />
            <text
              x={q5.x + q5.w / 2}
              y={top - 82}
              textAnchor="middle"
              fill={bpTextMain}
              fontFamily="var(--font-disp, 'Outfit', sans-serif)"
              fontSize="9"
              letterSpacing="0.08em"
              fontWeight="bold"
            >
              VVIP · VIP · OLD STEPS
            </text>
            <text
              x={q5.x + q5.w / 2}
              y={top - 70}
              textAnchor="middle"
              fill={bpSubFill}
              fontSize="7.5"
            >
              4 gates · NW / SW / S-rear
            </text>
            <rect
              x={q6.x - 2}
              y={top - 100}
              width={q6.w + 4}
              height={bh + 100}
              fill="none"
              stroke={bpAccentGold}
              strokeWidth="1.6"
              strokeDasharray="7 4"
            />
            <text
              x={q6.x + q6.w / 2}
              y={top - 80}
              textAnchor="middle"
              fill={bpTextMain}
              fontSize="10"
              fontWeight="bold"
            >
              అమ్మవారు
            </text>
            <text
              x={q6.x + q6.w / 2}
              y={top - 68}
              textAnchor="middle"
              fill={bpAccentCyan}
              fontSize="7.5"
              fontFamily="var(--font-mono, monospace)"
              fontWeight="bold"
              letterSpacing="0.05em"
            >
              SANCTUM
            </text>
          </g>

          {/* Lanes & Cells */}
          <g>
            {QLINES.map((l, i) => {
              const y = bpLaneY(i);
              const cleanName = l.name.replace(/^Q\d+\s*·\s*/, '');

              return (
                <g key={l.id}>
                  {S1.map(o => {
                    const isActive = STAGE_ACTIVE_LINES[o.st.id]?.includes(l.id);
                    const d = cellDens(l.id, o.st, queueState.cells);
                    const count = queueState.cells[qKey(l.id, o.st.id)] || 0;

                    if (!isActive) {
                      return (
                        <g key={o.st.id}>
                          <rect
                            x={o.x}
                            y={y}
                            width={o.w - 3}
                            height={BP.laneH}
                            fill={isLight ? "#E2E8F0" : "#08151E"}
                            stroke={bpCardBorder}
                            strokeWidth="0.8"
                            strokeDasharray="4 3"
                            opacity={0.35}
                          />
                          <text
                            x={o.x + o.w / 2}
                            y={y + 12}
                            textAnchor="middle"
                            fill={bpDimFill}
                            fontFamily="var(--font-mono, monospace)"
                            fontSize="9.5"
                            opacity={0.4}
                            style={{ pointerEvents: "none" }}
                          >
                            —
                          </text>
                        </g>
                      );
                    }

                    return (
                      <g key={o.st.id}>
                        <rect
                          className="cursor-pointer transition-[fill,stroke] duration-300 hover:stroke-blue-500 hover:stroke-[1.6]"
                          x={o.x}
                          y={y}
                          width={o.w - 3}
                          height={BP.laneH}
                          fill={losColor(d)}
                          fillOpacity={0.9}
                          stroke={bpCardBorder}
                          strokeWidth="1"
                          onClick={() => {
                            onSafeClick(() => {
                              logEvent("gate", `<b>${l.name}</b> at ${o.st.name} (${o.st.sector}) — ${fmt(count)} standing, ${crowdWord(d)}.`);
                            });
                          }}
                        >
                          <title>{`${l.name} · ${o.st.sector} (${o.st.name}) — ${fmt(count)} devotees`}</title>
                        </rect>
                        <text
                          x={o.x + o.w / 2}
                          y={y + 12}
                          textAnchor="middle"
                          fill={d > 2.2 ? "#2A0D0A" : "#04231B"}
                          fontFamily="var(--font-mono, monospace)"
                          fontSize="9.5"
                          fontWeight="bold"
                          style={{ pointerEvents: "none" }}
                        >
                          {fmt(count)}
                        </text>
                      </g>
                    );
                  })}
                  
                  {/* Left Lane Identity Pill */}
                  <g>
                    <rect
                      x={14}
                      y={y}
                      width={BP.x0 - 24}
                      height={BP.laneH}
                      rx="3"
                      fill={bpCardBg}
                      stroke={bpCardBorder}
                      strokeWidth="0.8"
                    />
                    <circle cx={24} cy={y + BP.laneH / 2} r="3" fill={l.col} />
                    <text
                      x={32}
                      y={y + BP.laneH / 2 + 3.5}
                      fill={bpTextMain}
                      fontFamily="var(--font-mono, monospace)"
                      fontSize="8.5"
                      fontWeight="bold"
                    >
                      {l.id}
                    </text>
                    <text
                      x={BP.x0 - 16}
                      y={y + BP.laneH / 2 + 3.5}
                      textAnchor="end"
                      fill={bpTextMuted}
                      fontFamily="var(--font-mono, monospace)"
                      fontSize="8"
                    >
                      {cleanName}
                    </text>
                  </g>
                </g>
              );
            })}
          </g>

          {/* ================= SANCTUM EXIT GATES (AFTER ANTHARALAYAM FLOW) ================= */}
          <g>
            {/* Main Exit Gates Enclosure Box */}
            <rect
              x={right + 12}
              y={top}
              width={210}
              height={bh}
              fill={bpGateBoxBg}
              stroke={bpGateBoxBorder}
              strokeWidth="1.2"
              rx="3"
            />

            {/* Top Gate Header Tag */}
            <text
              x={right + 18}
              y={top - 8}
              fontFamily="var(--font-mono, monospace)"
              fontSize="9"
            >
              <tspan fill={bpAccentGold} fontWeight="bold">S-10</tspan>
              <tspan fill={bpAccentCyan} letterSpacing="0.06em" dx="6" fontWeight="bold">
                SANCTUM EXIT GATES
              </tspan>
            </text>
            <text x={right + 18} y={top - 22} fill={bpSubFill} fontSize="8.5">
              నిష్క్రమణ ద్వారాలు · 5 Portals
            </text>

            {/* Sub-label above header */}
            <text
              x={right + 18}
              y={top - 36}
              fill={bpTitleFill}
              fontFamily="var(--font-mono, monospace)"
              fontSize="8"
              letterSpacing="0.08em"
            >
              ▶ REGULATED CLEARANCE
            </text>

            {/* Dimension guide */}
            <line x1={right + 12} y1={top + bh + 26} x2={right + 222} y2={top + bh + 26} stroke={bpBorderDash} strokeWidth="0.8" />
            <line x1={right + 12} y1={top + bh + 21} x2={right + 12} y2={top + bh + 31} stroke={bpBorderDash} strokeWidth="0.8" />
            <line x1={right + 222} y1={top + bh + 21} x2={right + 222} y2={top + bh + 31} stroke={bpBorderDash} strokeWidth="0.8" />
            <text
              x={right + 117}
              y={top + bh + 22}
              textAnchor="middle"
              fill={bpAccentCyan}
              fontFamily="var(--font-mono, monospace)"
              fontSize="8.5"
              fontWeight="bold"
            >
              5 EXIT GATES
            </text>

            {/* Individual Exit Gate Rows for each Q-Line */}
            {QLINES.map((l, i) => {
              const y = bpLaneY(i);
              const gateCamId = `CAM-S10-EG${i + 1}`;
              const lineOutflow = Math.round((darshanRate > 0 ? darshanRate : 230) * (l.share || 0.2));
              const destLabel = i <= 1 ? "Ravi Chettu" : i <= 3 ? "Sivalayam" : "Mouna Muni";

              return (
                <g key={`exit-gate-${l.id}`}>
                  {/* Gate Portal Background */}
                  <rect
                    x={right + 16}
                    y={y}
                    width={202}
                    height={BP.laneH}
                    fill={bpGateItemBg}
                    stroke={bpGateItemBorder}
                    strokeWidth="1"
                    rx="2"
                    className="cursor-pointer hover:stroke-blue-500 transition-colors"
                    onClick={() => {
                      onSafeClick(() => {
                        logEvent(
                          "gate",
                          `<b>Exit Gate EG-${i + 1} (${l.name})</b> passing ~${fmt(lineOutflow)} devotees/min towards ${destLabel}.`
                        );
                      });
                    }}
                  >
                    <title>{`Sanctum Exit Gate EG-${i + 1} (${l.name}) · ${destLabel}`}</title>
                  </rect>

                  {/* Gate ID Tag */}
                  <rect
                    x={right + 19}
                    y={y + 2}
                    width={32}
                    height={BP.laneH - 4}
                    fill={bpGateTagBg}
                    stroke={bpGateTagBorder}
                    strokeWidth="0.8"
                    rx="1.5"
                  />
                  <text
                    x={right + 35}
                    y={y + 11.5}
                    textAnchor="middle"
                    fill={bpGateTagText}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="8"
                    fontWeight="bold"
                  >
                    EG-{i + 1}
                  </text>

                  {/* Flow Arrows & Destination */}
                  <text
                    x={right + 56}
                    y={y + 11.5}
                    fill={bpTitleFill}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="7.5"
                    fontWeight="bold"
                    letterSpacing="0.02em"
                  >
                    ▶ {destLabel}
                  </text>

                  {/* Realtime Rate */}
                  <text
                    x={right + 184}
                    y={y + 11.5}
                    textAnchor="end"
                    fill={bpAccentGold}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="8"
                    fontWeight="bold"
                  >
                    {fmt(lineOutflow)}/m
                  </text>

                  {/* Gate CCTV Camera */}
                  <g
                    className="cursor-pointer group"
                    onClick={() => onSafeClick(() => openCamera({
                      id: gateCamId,
                      sector: "S-10",
                      place: `Sanctum Exit Gate EG-${i + 1} · ${l.name}`,
                      stage: "XS1",
                      line: l.id,
                      kind: "exit"
                    }))}
                  >
                    <circle
                      cx={right + 200}
                      cy={y + 8.5}
                      r="5.5"
                      fill={bpCamCircleFill}
                      stroke={bpCamCircleStroke}
                      strokeWidth="1"
                      className="group-hover:fill-blue-500 transition-colors"
                    />
                    <path
                      d={`M${right + 197.5},${y + 6.5} h4 v4 h-4 z M${right + 201.5},${y + 7.2} l2.2,-1 v3.6 l-2.2,-1 z`}
                      fill={bpCamIconFill}
                      className="group-hover:fill-neutral-900 dark:group-hover:fill-[#101B26] transition-colors"
                    />
                    <title>{`Open Camera for Exit Gate EG-${i + 1}`}</title>
                  </g>
                </g>
              );
            })}
          </g>

          {/* ================= FLOW CONNECTOR RAMP (SANCTUM GATES -> S-10 RAVI CHETTU DISPERSAL) ================= */}
          <g>
            {/* Outflow Flow Direction Indicator Badge */}
            <g transform={`translate(${right + 30}, ${top + bh + 48})`}>
              <rect
                x="0"
                y="0"
                width="174"
                height="34"
                rx="3"
                fill={bpOutflowBadgeBg}
                stroke={bpOutflowBadgeBorder}
                strokeWidth="1.2"
              />
              <text
                x="87"
                y="14"
                textAnchor="middle"
                fill={bpAccentCyan}
                fontFamily="var(--font-mono, monospace)"
                fontSize="8.5"
                fontWeight="bold"
                letterSpacing="0.06em"
              >
                ▼ DISPERSAL OUTFLOW ▼
              </text>
              <text
                x="87"
                y="26"
                textAnchor="middle"
                fill={bpTextMuted}
                fontSize="8"
              >
                రావి చెట్టు బహిరంగ ప్రదేశం మార్గం (S-10)
              </text>
            </g>

            {/* Curved Outflow Stream Path */}
            <path
              d={`M ${right + 117} ${top + bh + 84} C ${right + 117} ${top + bh + 160}, ${right + 130} ${BP.exitY - 70}, ${right + 130} ${BP.exitY - 4}`}
              fill="none"
              stroke={isLight ? "#059669" : "#00F0B5"}
              strokeWidth="2.2"
              strokeDasharray="6 4"
              opacity="0.85"
            />
            <path
              d={`M ${right + 117} ${top + bh + 84} C ${right + 117} ${top + bh + 160}, ${right + 130} ${BP.exitY - 70}, ${right + 130} ${BP.exitY - 4}`}
              fill="none"
              stroke={bpAccentGold}
              strokeWidth="4"
              strokeDasharray="14 60"
              opacity="0.6"
              style={{ filter: `drop-shadow(0 0 6px ${isLight ? '#059669' : '#00F0B5'})` }}
            />
          </g>

          {/* Camera Buttons on Lanes */}
          <g>
            {S1.map(o =>
              QLINES.map((l, i) => {
                const isActive = STAGE_ACTIVE_LINES[o.st.id]?.includes(l.id);
                if (!isActive) return null;
                const camId = `CAM-${o.st.sector.replace("S-", "S")}-${l.id}`;
                return (
                  <g
                    key={`${o.st.id}-${l.id}`}
                    className="cursor-pointer group"
                    onClick={() => onSafeClick(() => openCamera(camId))}
                  >
                    <circle
                      cx={o.x + o.w - 11}
                      cy={bpLaneY(i) + 8}
                      r="6.4"
                      fill={bpCamCircleFill}
                      stroke={bpCamCircleStroke}
                      strokeWidth="1"
                      className="group-hover:fill-blue-500 transition-colors"
                    />
                    <path
                      d={`M${o.x + o.w - 14.2},${bpLaneY(i) + 5.6} h4.8 v4.8 h-4.8 z M${o.x + o.w - 9.1},${bpLaneY(i) + 6.4} l2.9,-1.4 v5.6 l-2.9,-1.4 z`}
                      fill={bpCamIconFill}
                      className="group-hover:fill-neutral-900 dark:group-hover:fill-[#101B26] transition-colors"
                    />
                    <title>{o.st.sector} · {l.name} — open camera</title>
                  </g>
                );
              })
            )}
          </g>

          {/* Features */}
          <g>
            {BP_FEATURES.map((f, idx) => {
              const o = S1.find(z => z.st.id === f.s);
              if (!o) return null;
              const x = o.x + o.w * f.o;
              const isUpper = f.side < 0;
              const y = isUpper
                ? (idx % 2 === 0 ? top - 66 : top - 90)
                : (idx % 2 === 0 ? top + bh + 62 : top + bh + 86);
              const ly = isUpper ? top - 6 : top + bh + 6;

              // Split long titles cleanly to prevent horizontal overlap
              const splitTitle = (title: string): string[] => {
                if (title.length <= 15) return [title];
                if (title.includes(" · ")) return title.split(" · ");
                if (title.includes(" & ")) return title.split(" & ");
                const mid = title.lastIndexOf(" ", 15);
                if (mid !== -1) return [title.slice(0, mid), title.slice(mid + 1)];
                return [title];
              };
              const titleLines = splitTitle(f.t);

              return (
                <g key={idx}>
                  <line
                    x1={x}
                    y1={ly}
                    x2={x}
                    y2={isUpper ? y + 14 : y - 16}
                    stroke={bpBorderDash}
                    strokeWidth="0.7"
                    strokeDasharray="3 3"
                  />
                  <circle cx={x} cy={ly} r="2.3" fill={bpAccentCyan} />
                  {f.g && (
                    <>
                      <rect
                        x={x - 15}
                        y={y - 13}
                        width="30"
                        height="13"
                        rx="2"
                        fill="none"
                        stroke={bpCamCircleStroke}
                        strokeWidth="0.9"
                      />
                      <text
                        x={x}
                        y={y - 3.2}
                        textAnchor="middle"
                        fill={bpCamIconFill}
                        fontFamily="var(--font-mono, monospace)"
                        fontSize="8"
                        fontWeight="bold"
                      >
                        {GLYPH[f.g]}
                      </text>
                    </>
                  )}
                  {titleLines.map((tl, tlIdx) => (
                    <text
                      key={tlIdx}
                      x={x}
                      y={y + (f.g ? 10 : 0) + tlIdx * 9.5}
                      textAnchor="middle"
                      fill={bpTextMuted}
                      fontFamily="var(--font-mono, monospace)"
                      fontSize="7.5"
                      letterSpacing="0.04em"
                    >
                      {tl}
                    </text>
                  ))}
                  {f.sub && (
                    <text
                      x={x}
                      y={y + (f.g ? 10 : 0) + titleLines.length * 9.5}
                      textAnchor="middle"
                      fill={bpDimFill}
                      fontSize="7"
                    >
                      {f.sub}
                    </text>
                  )}
                </g>
              );
            })}
          </g>

          {/* Exit Route Section (Exit 1 & Exit 2) */}
          <g>
            {/* Header for Exit 1 (Main Corridor) */}
            <text
              x={BP.x0}
              y={BP.exitY - 56}
              fill={bpTitleFill}
              fontFamily="var(--font-disp, 'Outfit', sans-serif)"
              fontSize="15"
              letterSpacing="0.08em"
              fontWeight="bold"
            >
              EXIT 1 (MAIN ROUTE) · KANAKA DURGA NAGAR DISPERSAL (S-10 TO S-15)
            </text>
            <text
              x={BP.x0}
              y={BP.exitY - 38}
              fill={bpSubFill}
              fontSize="11"
              letterSpacing="0.03em"
            >
              నిష్క్రమణ మార్గం 1 · Antharalayam ▶ Ravi Chettu ▶ Sivalayam ▶ KD Nagar · one-way, no return
            </text>

            {/* Dedicated EXIT 2: AVADHUTHA MOUNA MUNI DIRECT RAMP EGRESS (S-11) */}
            {(() => {
              const mmCount = queueState.xcells['XS_MM'] || 148;
              const mmDens = mmCount / 120;
              const mmX = right - 380;
              const mmY = BP.exitY - 76;
              const mmW = 380;
              const mmH = 46;

              return (
                <g key="exit2-mouna-muni">
                  {/* Exit 2 Outer Frame Box */}
                  <rect
                    x={mmX}
                    y={mmY}
                    width={mmW}
                    height={mmH}
                    fill={bpGateBoxBg}
                    stroke={bpAccentCyan}
                    strokeWidth="1.2"
                    strokeDasharray="4 3"
                    rx="3"
                  />
                  
                  {/* Density Fill Channel */}
                  <rect
                    className="cursor-pointer transition-[fill,stroke] duration-300 hover:stroke-blue-500 hover:stroke-[1.6]"
                    x={mmX + 3}
                    y={mmY + 3}
                    width={mmW - 6}
                    height={mmH - 6}
                    fill={losColor(mmDens)}
                    fillOpacity="0.85"
                    stroke={bpCardBorder}
                    strokeWidth="1"
                    rx="2"
                    onClick={() => {
                      onSafeClick(() => {
                        logEvent(
                          "gate",
                          `<b>Exit 2 · Avadhutha Mouna Muni Ramp (S-11)</b> — ${fmt(mmCount)} standing devotees on direct egress descent.`
                        );
                      });
                    }}
                  >
                    <title>Exit 2 · Avadhutha Mouna Muni Direct Egress Ramp (S-11)</title>
                  </rect>

                  {/* Exit 2 Title Tags */}
                  <text
                    x={mmX + 8}
                    y={mmY - 10}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="9.5"
                    fontWeight="bold"
                  >
                    <tspan fill={bpAccentGold}>EXIT 2 (S-11)</tspan>
                    <tspan fill={bpAccentCyan} dx="6" letterSpacing="0.04em">
                      AVADHUTHA MOUNA MUNI DIRECT RAMP EGRESS
                    </tspan>
                  </text>
                  <text
                    x={mmX + 8}
                    y={mmY - 22}
                    fill={bpDimFill}
                    fontSize="8"
                  >
                    అవధూత మౌన ముని డైరెక్ట్ ఎగ్జిట్ రాంప్ · Dedicated Direct Descent
                  </text>

                  {/* Realtime Headcount and Rate */}
                  <text
                    x={mmX + mmW / 2 - 20}
                    y={mmY + mmH / 2 + 5}
                    textAnchor="middle"
                    fill={mmDens > 2.2 ? "#2A0D0A" : "#04231B"}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="13"
                    fontWeight="bold"
                    style={{ pointerEvents: "none" }}
                  >
                    {fmt(mmCount)} DEVOTEES · {crowdWord(mmDens).toUpperCase()}
                  </text>

                  {/* Mouna Muni CCTV Camera */}
                  <g
                    className="cursor-pointer group"
                    onClick={() => onSafeClick(() => openCamera({
                      id: "CAM-S11-MOUNAMUNI",
                      sector: "S-11",
                      place: "Mouna Muni Direct Egress Ramp",
                      stage: "XS1",
                      kind: "exit"
                    }))}
                  >
                    <circle
                      cx={mmX + mmW - 16}
                      cy={mmY + mmH / 2}
                      r="7.5"
                      fill={bpCamCircleFill}
                      stroke={bpCamCircleStroke}
                      strokeWidth="1.2"
                      className="group-hover:fill-blue-500 transition-colors"
                    />
                    <path
                      d={`M${mmX + mmW - 19.5},${mmY + mmH / 2 - 2.8} h5.5 v5.5 h-5.5 z M${mmX + mmW - 13.8},${mmY + mmH / 2 - 1.8} l3.2,-1.6 v6.4 l-3.2,-1.6 z`}
                      fill={bpCamIconFill}
                      className="group-hover:fill-neutral-900 dark:group-hover:fill-[#101B26] transition-colors"
                    />
                    <title>Open Camera · Mouna Muni Egress Ramp (CAM-S11-MOUNAMUNI)</title>
                  </g>
                </g>
              );
            })()}

            {/* Exit 1 Stages (XS1 to XS7) */}
            {X1.map(o => {
              const d = xDens(o.st, queueState.xcells);
              const count = queueState.xcells[o.st.id] || 0;
              const camId = `CAM-${o.st.sector.replace("S-", "S")}-X1`;
              return (
                <g key={o.st.id}>
                  <rect
                    className="cursor-pointer transition-[fill,stroke] duration-300 hover:stroke-blue-500 hover:stroke-[1.6]"
                    x={o.x}
                    y={BP.exitY}
                    width={o.w - 3}
                    height={BP.exitH}
                    fill={losColor(d)}
                    fillOpacity="0.9"
                    stroke={bpCardBorder}
                    strokeWidth="1"
                    onClick={() => {
                      onSafeClick(() => {
                        logEvent("gate", `<b>${o.st.name}</b> (${o.st.sector}) — ${fmt(count)} on the exit route, ${crowdWord(d)}.`);
                      });
                    }}
                  >
                    <title>{o.st.name}</title>
                  </rect>
                  <rect x={o.x - 1} y={BP.exitY - 1} width={o.w} height={BP.exitH + 2} fill="none" stroke={bpCardBorder} strokeWidth="1" rx="2" />
                  <text
                    x={o.x + o.w / 2}
                    y={BP.exitY + BP.exitH / 2 + 5}
                    textAnchor="middle"
                    fill={d > 2.2 ? "#2A0D0A" : "#04231B"}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="15"
                    fontWeight="bold"
                    style={{ pointerEvents: "none" }}
                  >
                    {fmt(count)}
                  </text>
                  <text
                    x={o.x + 4}
                    y={BP.exitY - 10}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="9.5"
                  >
                    <tspan fill={bpAccentGold} fontWeight="bold">{o.st.sector}</tspan>
                    <tspan fill={bpDimFill} fontSize="9" dx="8">{o.st.te}</tspan>
                  </text>
                  <line x1={o.x} y1={BP.exitY + BP.exitH + 24} x2={o.x + o.w} y2={BP.exitY + BP.exitH + 24} stroke={bpBorderDash} strokeWidth="0.8" />
                  <text
                    x={o.x + o.w / 2}
                    y={BP.exitY + BP.exitH + 20}
                    textAnchor="middle"
                    fill={bpSubFill}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="8.5"
                  >
                    {o.st.len} m
                  </text>
                  <g className="cursor-pointer group" onClick={() => onSafeClick(() => openCamera(camId))}>
                    <circle
                      cx={o.x + o.w - 14}
                      cy={BP.exitY + 16}
                      r="6.4"
                      fill={bpCamCircleFill}
                      stroke={bpCamCircleStroke}
                      strokeWidth="1"
                      className="group-hover:fill-blue-500 transition-colors"
                    />
                    <path
                      d={`M${o.x + o.w - 17.2},${BP.exitY + 13.6} h4.8 v4.8 h-4.8 z M${o.x + o.w - 12.1},${BP.exitY + 14.4} l2.9,-1.4 v5.6 l-2.9,-1.4 z`}
                      fill={bpCamIconFill}
                      className="group-hover:fill-neutral-900 dark:group-hover:fill-[#101B26] transition-colors"
                    />
                  </g>
                </g>
              );
            })}

            {BP_EXITF.map((f, idx) => {
              const o = X1.find(z => z.st.id === f.s);
              if (!o) return null;
              const x = o.x + o.w / 2;
              const d = xDens(o.st, queueState.xcells);

              // Split long descriptions to prevent cross-column overlap
              const splitDesc = (title: string): string[] => {
                if (title.length <= 15) return [title];
                if (title.includes(" · ")) return title.split(" · ");
                if (title.includes(" & ")) return title.split(" & ");
                const mid = title.lastIndexOf(" ", 15);
                if (mid !== -1) return [title.slice(0, mid), title.slice(mid + 1)];
                return [title];
              };
              const descLines = splitDesc(f.t);
              const isDarkLos = d > 2.8;
              const textColor = isDarkLos ? "#FFFFFF" : "#04231B";

              return (
                <g key={idx}>
                  {f.g && (
                    <>
                      <rect
                        x={x - 16}
                        y={BP.exitY + BP.exitH - 42}
                        width="32"
                        height="13"
                        rx="2"
                        fill={isDarkLos ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.08)"}
                        stroke={textColor}
                        strokeWidth="0.9"
                      />
                      <text
                        x={x}
                        y={BP.exitY + BP.exitH - 32}
                        textAnchor="middle"
                        fill={textColor}
                        fontFamily="var(--font-mono, monospace)"
                        fontSize="8"
                        fontWeight="bold"
                      >
                        {GLYPH[f.g]}
                      </text>
                    </>
                  )}
                  {descLines.map((line, lIdx) => (
                    <text
                      key={lIdx}
                      x={x}
                      y={BP.exitY + BP.exitH - (descLines.length === 1 ? 12 : 20 - lIdx * 10)}
                      textAnchor="middle"
                      fill={textColor}
                      fontFamily="var(--font-mono, monospace)"
                      fontSize="8"
                      letterSpacing="0.04em"
                      fontWeight="bold"
                    >
                      {line}
                    </text>
                  ))}
                </g>
              );
            })}

            <text
              x={BP.x0}
              y={BP.exitY + BP.exitH + 42}
              fill={isLight ? "#64748B" : "#CBD5E1"}
              fontSize="8.5"
              fontFamily="var(--font-mono, monospace)"
            >
              S-10 Ravi Chettu · S-11 Nataraja Swamy → Avadhutha Mouna Swamy (₹500/₹300 and Free/₹100 lines, VIP exit ramp) sit between Antharalayam and the saree counter
            </text>
          </g>

          {/* Return & Dispersal Band */}
          <g>
            <text
              x={BP.x0}
              y={ry - 14}
              fill={bpTitleFill}
              fontFamily="var(--font-disp, 'Outfit', sans-serif)"
              fontSize="10.5"
              letterSpacing="0.08em"
              fontWeight="bold"
            >
              RETURN &amp; DISPERSAL — east lane, opposite the entry lines · సెక్టార్లు S-18 · S-19
            </text>
            {BP_RETURN.map((r, i) => {
              const x = BP.x0 + i * rw;
              return (
                <g key={i}>
                  <rect x={x} y={ry} width={rw - 7} height="42" fill={bpCardBg} stroke={bpCardBorder} strokeWidth="1" rx="2" />
                  <text
                    x={x + 7}
                    y={ry + 16}
                    fill={bpAccentGold}
                    fontFamily="var(--font-mono, monospace)"
                    fontSize="9"
                    fontWeight="bold"
                  >
                    {r.c}
                  </text>
                  <text x={x + 7} y={ry + 31} fill={bpTextMuted} fontSize="8.5">{r.n}</text>
                  {i < BP_RETURN.length - 1 && (
                    <path d={`M${x + rw - 6},${ry + 21} l-5,-4 v8 z`} fill={bpBorderDash} />
                  )}
                  <title>{`${r.c}: ${r.n}`}</title>
                </g>
              );
            })}
          </g>

          {/* Scale */}
          <g>
            <line x1={sx} y1={sy} x2={sx + 200} y2={sy} stroke={bpSubFill} strokeWidth="1" />
            <line x1={sx} y1={sy - 5} x2={sx} y2={sy + 5} stroke={bpSubFill} strokeWidth="1" />
            <line x1={sx + 100} y1={sy - 4} x2={sx + 100} y2={sy + 4} stroke={bpSubFill} strokeWidth="1" />
            <line x1={sx + 200} y1={sy - 5} x2={sx + 200} y2={sy + 5} stroke={bpSubFill} strokeWidth="1" />
            <text x={sx} y={sy + 18} fill={bpSubFill} fontFamily="var(--font-mono, monospace)" fontSize="8.5">0</text>
            <text x={sx + 100} y={sy + 18} textAnchor="middle" fill={bpSubFill} fontFamily="var(--font-mono, monospace)" fontSize="8.5">100 m</text>
            <text x={sx + 200} y={sy + 18} textAnchor="middle" fill={bpSubFill} fontFamily="var(--font-mono, monospace)" fontSize="8.5">200 m</text>
            <text x={sx + 216} y={sy + 4} fill={bpSubFill} fontFamily="var(--font-mono, monospace)" fontSize="8.5">along-route 1:1 · lane pitch exaggerated</text>
          </g>

          {/* Title Block */}
          <g transform={`translate(${BP.W - 436},${BP.H - 136})`}>
            <rect x="0" y="0" width="414" height="114" fill={bpCardBg} stroke={bpCardBorder} strokeWidth="1" rx="3" />
            <line x1="0" y1="30" x2="414" y2="30" stroke={bpCardBorder} strokeWidth="0.8" />
            <line x1="252" y1="0" x2="252" y2="114" stroke={bpCardBorder} strokeWidth="0.8" />
            <text
              x="10"
              y="20"
              fill={bpTextMuted}
              fontFamily="var(--font-disp, 'Outfit', sans-serif)"
              fontSize="9.5"
              letterSpacing="0.04em"
              fontWeight="bold"
            >
              SRI DURGA MALLESWARA SWAMY VARLA DEVASTHANAM · INDRAKEELADRI
            </text>
            <text
              x="10"
              y="50"
              fill={bpSubFill}
              fontFamily="var(--font-mono, monospace)"
              fontSize="7.5"
              letterSpacing="0.16em"
            >
              DRAWING
            </text>
            <text
              x="10"
              y="66"
              fill={bpTextMain}
              fontFamily="var(--font-disp, 'Outfit', sans-serif)"
              fontSize="10.5"
              fontWeight="bold"
            >
              Darshan queue system — plan
            </text>
            <text
              x="10"
              y="88"
              fill={bpSubFill}
              fontFamily="var(--font-mono, monospace)"
              fontSize="7.5"
              letterSpacing="0.16em"
            >
              BASIS
            </text>
            <text
              x="10"
              y="104"
              fill={bpTextMain}
              fontFamily="var(--font-disp, 'Outfit', sans-serif)"
              fontSize="10"
            >
              Dasara sector sheets S-4 … S-17
            </text>
            <text
              x="262"
              y="50"
              fill={bpSubFill}
              fontFamily="var(--font-mono, monospace)"
              fontSize="7.5"
              letterSpacing="0.16em"
            >
              DRG No
            </text>
            <text
              x="262"
              y="66"
              fill={bpTextMain}
              fontFamily="var(--font-mono, monospace)"
              fontSize="10"
            >
              RST-QL-001 Rev A
            </text>
            <text
              x="262"
              y="88"
              fill={bpSubFill}
              fontFamily="var(--font-mono, monospace)"
              fontSize="7.5"
              letterSpacing="0.16em"
            >
              STATUS
            </text>
            <text
              x="262"
              y="104"
              fill={isLight ? "#059669" : "#25D0A3"}
              fontFamily="var(--font-mono, monospace)"
              fontSize="10"
              fontWeight="bold"
            >
              ● LIVE  {hhmm(clock)}
            </text>
          </g>
        </svg>
      </div>
    </div>
    <CameraModal />
  </div>
);
};

export const DigitalTwinView: React.FC = () => {
  return (
    <CrowdProvider>
      <DigitalTwinViewInner />
    </CrowdProvider>
  );
};
